import { Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconArrowRight, IconUsers } from '@tabler/icons-react';
import type { SpreadSessionView } from '@creativitas/shared';

/**
 * Очередь участников в групповой МАК-сессии (§бэклог 9).
 *
 * Дарья описала три способа вести встречу: один участник вытягивает все свои
 * карты и передаёт ход дальше; участники чередуются по одной; ведущий сам
 * называет следующего. Это один и тот же вопрос — «чья сейчас очередь», — и
 * отвечает на него ведущий, а не расписание в коде.
 *
 * Свободный порядок остаётся исходным состоянием. Очередь нужна не каждой
 * встрече, и включать её всем значило бы решать за ведущего то, что он решает
 * по ходу дела.
 */
export function SpreadTurnControl({
  session,
  busy,
  onSet,
}: {
  session: SpreadSessionView;
  busy: boolean;
  onSet: (participantId: string | null) => void;
}) {
  const players = session.participants.filter((participant) => participant.kind !== 'facilitator');
  if (players.length === 0) return null;

  const activeIndex = players.findIndex(
    (participant) => participant.id === session.activeParticipantId,
  );
  /* Из свободного порядка «Следующий» начинает очередь с первого вошедшего. */
  const next = players[(activeIndex + 1) % players.length];
  const free = session.activeParticipantId === null;

  return (
    <Paper withBorder p="md" radius="lg" data-testid="spread-turn">
      <Stack gap="sm">
        <Group gap="xs">
          <IconUsers size={18} color="var(--accent)" />
          <Title order={4}>Очередь</Title>
        </Group>

        {/*
          На тесте 16 сентября имя в этом блоке нажимали, чтобы перейти к столу
          участника, — и нечаянно отдавали ему очередь, запирая остальных.
          Поэтому текст говорит прямо, что делает нажатие на имя, а стол
          выбирается отдельно — в «Чей расклад на столе».
        */}
        <Text size="sm" c="var(--text-muted)" data-testid="spread-turn-hint">
          {free
            ? 'Свободный порядок: карту может вытянуть любой участник. Нажмите на имя, чтобы тянул только этот человек.'
            : `Сейчас тянет только ${
                players.find((participant) => participant.id === session.activeParticipantId)
                  ?.displayName ?? 'выбранный участник'
              }, остальные ждут. «Свободный порядок» снимает очередь.`}
        </Text>

        <Group gap="xs" wrap="wrap">
          <Button
            size="compact-sm"
            variant={free ? 'filled' : 'default'}
            disabled={busy}
            onClick={() => onSet(null)}
          >
            Свободный порядок
          </Button>
          {players.map((participant) => (
            <Button
              key={participant.id}
              size="compact-sm"
              variant={participant.id === session.activeParticipantId ? 'filled' : 'default'}
              disabled={busy}
              onClick={() => onSet(participant.id)}
            >
              {participant.displayName}
            </Button>
          ))}
          {next ? (
            <Button
              size="compact-sm"
              variant="light"
              rightSection={<IconArrowRight size={14} />}
              disabled={busy}
              onClick={() => onSet(next.id)}
            >
              Следующий
            </Button>
          ) : null}
        </Group>
      </Stack>
    </Paper>
  );
}
